import React, { useState } from 'react';

const InteractiveBrain: React.FC = () => {
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);

  const nodes = [
    { id: 'strategy', x: 100, y: 100, label: 'Strategy', color: '#FF6B6B' },
    { id: 'engineering', x: 200, y: 50, label: 'Engineering', color: '#3D5AF1' },
    { id: 'analytics', x: 300, y: 100, label: 'Analytics', color: '#00D4FF' },
    { id: 'ai', x: 200, y: 180, label: 'AI/ML', color: '#4ECDC4' },
  ];

  const connections = [
    { from: 'strategy', to: 'engineering' },
    { from: 'engineering', to: 'analytics' },
    { from: 'analytics', to: 'ai' },
    { from: 'ai', to: 'strategy' },
  ];

  return (
    <div className="relative w-full h-[400px] md:h-[500px] bg-gradient-to-br from-white to-gray-50 rounded-3xl border border-gray-200 p-8">
      <svg width="100%" height="100%" viewBox="0 0 400 300" className="absolute inset-0">
        {/* Connections */}
        {connections.map((conn) => {
          const fromNode = nodes.find(n => n.id === conn.from);
          const toNode = nodes.find(n => n.id === conn.to);
          if (!fromNode || !toNode) return null;
          
          return (
            <line
              key={`${conn.from}-${conn.to}`}
              x1={fromNode.x}
              y1={fromNode.y}
              x2={toNode.x}
              y2={toNode.y}
              stroke="url(#gradient)"
              strokeWidth="2"
              strokeDasharray="5,3"
            />
          );
        })}

        {/* Nodes */}
        {nodes.map((node) => (
          <g
            key={node.id}
            onMouseEnter={() => setHoveredNode(node.id)}
            onMouseLeave={() => setHoveredNode(null)}
            className="cursor-pointer"
          >
            <circle
              cx={node.x}
              cy={node.y}
              r={hoveredNode === node.id ? 15 : 12}
              fill={node.color}
              className="transition-all duration-300"
            />
            
            {/* Node label */}
            <text
              x={node.x}
              y={node.y - 20}
              textAnchor="middle"
              className="text-sm font-bold fill-gray-800"
            >
              {node.label}
            </text>
          </g>
        ))}

        {/* Data particles */}
        {[...Array(10)].map((_, i) => (
          <circle
            key={i}
            cx={50 + i * 35}
            cy={250}
            r="3"
            fill="#00D4FF"
            className="animate-pulse"
            style={{ animationDelay: `${i * 0.2}s` }}
          />
        ))}

        <defs>
          <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#FF6B6B" />
            <stop offset="100%" stopColor="#4ECDC4" />
          </linearGradient>
        </defs>
      </svg>

      {/* Info panel */}
      <div className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-lg">
        {hoveredNode ? (
          <div>
            <h3 className="font-bold text-lg mb-2">
              {nodes.find(n => n.id === hoveredNode)?.label}
            </h3>
            <p className="text-sm text-gray-600">
              {hoveredNode === 'strategy' && 'Data strategy and architecture planning'}
              {hoveredNode === 'engineering' && 'Modern data stack implementation'}
              {hoveredNode === 'analytics' && 'Business intelligence and insights'}
              {hoveredNode === 'ai' && 'AI/ML model development'}
            </p>
          </div>
        ) : (
          <p className="text-gray-600 text-center">
            Hover over a node to explore our capabilities
          </p>
        )}
      </div>
    </div>
  );
};

export default InteractiveBrain;